// Profil-Nachbau F-25 Entwurf C (output/profil-team-20261014/cockpit-grafiken-20261019/SCHNITTSTELLEN-C.md 3.3; Entwurf
// mittel/seiten-c.js:170-284): Rechnungen und Sätze des Plattform-Cockpits im Stil „Ziel und Fortschritt“. Ziel „aktive Firmen“ (Prozent
// der Firmen mit Zugang), „Marge positiv“ über die Kostendeckung (Umsatz geteilt durch KI-Kosten), Anteil „hilfreich“ der Antwortqualität,
// Schritt und Zeile je Firma, Schritte der Übersicht, Tarife und die vier Prüfungen. Rein, ohne React, ohne Netz, ohne Zufall. Beträge
// formatiert nur die Seite (Angabe betrag, netMoney in PlattformCockpit.tsx); nur Firmen, nie Personen; Links nur auf vorhandene Seiten.
import { AUFMERKSAMKEIT, BERICHT_TEILE, KENNZAHL, ZIEL, ZIEL_PLATTFORM as W } from './cockpitTexte';
import type { PlattformAufmerksamkeit } from './cockpitApi';
import { datum, datumWien, prozent, type Trend, trendText, zahl, zahlEineStelle } from './format';
import { fortschritt } from './ziel';
import type { SchrittSymbol } from './ZielBausteine';
import { reiterSetzen } from './zeitraum';

export type Betrag = (cent: number) => string;
/** Grenzen wie der Server (backend cockpit/ziele.ts ZIEL_GRENZEN) für das Formular des Zielblocks. */
export const PLATTFORM_ZIEL_GRENZEN = { AKTIVE_FIRMEN: [1, 100], KOSTENDECKUNG: [1, 1000], ANTWORTEN_MIT_QUELLE: [1, 100] } as const;
/** Beispielwert ohne gesetztes Ziel (AUFTRAEGE „Offen“): 100 % für aktive Firmen und Kostendeckung. */
export const PLATTFORM_BEISPIEL = 100;
/** Angaben für ZielBlock (ZielBausteine.tsx); prozent null = ohne Balken und ohne Skala. */
export type ZielAngaben = { titel: string; gross: string; von: string; prozent: number | null; skala?: { links: string; mitte: string; rechts: string };
  satz: string; fuss?: string; art: 'gut' | 'knapp'; festlegen?: { beispiel: number } };
export type LinkZiel = { art: 'firma'; id: string } | { art: 'abrechnung' } | { art: 'teil'; teil: 'firmen' | 'aufmerksamkeit' };
export type SchrittLink = { text: string; ziel: LinkZiel };
/** Ein Schritt für „Was als Nächstes hilft“ (Schritte in ZielBausteine.tsx); den Link baut die Seite aus schrittAdresse. */
export type PlattformSchritt = { key: string; symbol: SchrittSymbol; stark: string; text?: string; link?: SchrittLink };
/** Eine Zeile von …/firmen (FirmenZeile in cockpitApi.ts passt); Beträge nur mit Euro-Recht. */
export type FirmaStand = { id: string; name: string; tarifName: string; recherchen: number; nachfragen: number; trend: Trend;
  plaetze: { belegt: number; gesamt: number | null }; kostenCent?: number; umsatzCent?: number };
/** Eine Zeile von …/tarife mit Euro-Recht. */
export type TarifStand = { tarif: string; name: string; plaetzeBelegt: number; recherchenJePlatz: number | null; kostenCent: number; umsatzCent: number };
/** Die Antwort von /system/quality, so weit die Rechnung sie braucht (Antwortqualitaet aus PF-GC-seite-3 passt). */
export type QualitaetZeilen = { items: { total: number; up: number | null; rate: number | null }[] };
export type PruefZeile = { key: 'zahlung' | 'kontingent' | 'inaktiv' | 'testphase'; art: 'hinweis' | 'ok'; titel: string; text: string };

const ABRECHNUNG: LinkZiel = { art: 'abrechnung' };
const firmaZiel = (id: string): LinkZiel => ({ art: 'firma', id });
/** Zielwert in Prozent für die Skala: „Ziel 100 %“. */
const zielProzent = (ziel: number) => ZIEL.zielWert(prozent(ziel));

/** Adresse eines Links: eine Firma in der Verwaltung, die Kundenabrechnung oder ein Teil des Cockpits (Zeitraum und Abfrage bleiben). */
export function schrittAdresse(ziel: LinkZiel, suche: URLSearchParams): string {
  if (ziel.art === 'firma') return `/verwaltung?organisation=${encodeURIComponent(ziel.id)}`;
  if (ziel.art === 'abrechnung') return '/verwaltung/abrechnung';
  return `?${reiterSetzen(suche, ziel.teil, 'uebersicht')}`;
}

/** Ziel „aktive Firmen“: Prozent der Firmen mit Zugang, aufgerundet auf ganze Firmen; ohne Ziel ohne Balken. */
export function aktiveFirmenZiel(l: { aktiv: number; mitZugang: number; ziel: number | null; davor?: number | null }): ZielAngaben {
  const { aktiv, mitZugang, ziel } = l;
  const titel = ziel === null ? KENNZAHL.aktiveFirmen : ziel === 100 ? W.aktivTitelAlle : W.aktivTitelTeil(ziel);
  const kopf = { titel, gross: zahl(aktiv), von: W.aktivVon(mitZugang, aktiv), art: 'gut' as const };
  if (mitZugang === 0) return { ...kopf, prozent: null, satz: BERICHT_TEILE.firmen.keineMitZugang };
  const satz = [aktiv >= mitZugang ? W.alleAktiv : BERICHT_TEILE.firmen.still(mitZugang - aktiv),
    ...(typeof l.davor === 'number' ? [W.davor(l.davor)] : [])].join(' ');
  if (ziel === null) return { ...kopf, prozent: null, satz, festlegen: { beispiel: PLATTFORM_BEISPIEL } };
  const soll = Math.max(1, Math.ceil(mitZugang * ziel / 100)), p = fortschritt(aktiv, soll);
  return { ...kopf, prozent: p, skala: { links: '0', mitte: ZIEL.skalaErreicht(p), rechts: ZIEL.zielWert(zahl(soll)) }, satz };
}

/** „Marge positiv“ über die Kostendeckung (Umsatz geteilt durch KI-Kosten), nur mit Euro-Recht. */
export function kostendeckungZiel(l: { kostenCent: number; umsatzCent: number; recherchen: number; ziel: number | null; kostenDavorCent?: number },
  betrag: Betrag): ZielAngaben {
  const { kostenCent, umsatzCent, ziel } = l;
  const deckung = kostenCent > 0 ? Math.round(umsatzCent * 100 / kostenCent) : null;
  const titel = ziel === null ? KENNZAHL.marge : ziel === 100 ? W.deckungTitelPositiv : W.deckungTitelZiel(ziel);
  const teile: string[] = kostenCent > 0
    ? [W.geldSatz(betrag(kostenCent), betrag(umsatzCent)), ...(l.recherchen > 0 ? [W.jeRecherche(betrag(Math.round(kostenCent / l.recherchen)))] : [])]
    : [W.ohneKosten(betrag(umsatzCent))];
  if (l.kostenDavorCent !== undefined) teile.push(W.kostenDavor(betrag(l.kostenDavorCent)));
  const kopf = { titel, gross: betrag(umsatzCent - kostenCent), von: W.margeImZeitraum, satz: teile.join(' '), fuss: W.deckungFuss };
  if (ziel === null) return { ...kopf, prozent: null, art: 'gut', festlegen: { beispiel: PLATTFORM_BEISPIEL } };
  if (deckung === null) return { ...kopf, prozent: null, art: 'gut' };
  const p = fortschritt(deckung, ziel);
  return { ...kopf, prozent: p, skala: { links: '0', mitte: W.deckt(deckung), rechts: zielProzent(ziel) }, art: deckung >= ziel ? 'gut' : 'knapp' };
}

/** Anteil „hilfreich“ wie system/Quality.tsx: nur Zeilen mit Anteil (ab minCell Bewertungen). */
export function hilfreichAnteil(q: QualitaetZeilen): { prozent: number | null; bewertungen: number } {
  const gezaehlt = q.items.filter(r => r.rate !== null && r.up !== null);
  const bewertungen = gezaehlt.reduce((summe, r) => summe + r.total, 0);
  const hilfreich = gezaehlt.reduce((summe, r) => summe + (r.up ?? 0), 0);
  return { prozent: bewertungen > 0 ? Math.round(hilfreich * 100 / bewertungen) : null, bewertungen };
}

/** KI-Kosten und Umsatz je Abschnitt zusammengezählt; ohne Euro-Recht (Feld fehlt) null. */
export function geldZusammengezaehlt(reihen: { kostenCent?: number; umsatzCent?: number }[]): { kosten: number[]; umsatz: number[] } | null {
  if (reihen.some(r => r.kostenCent === undefined || r.umsatzCent === undefined)) return null;
  const kosten: number[] = [], umsatz: number[] = [];
  let k = 0, u = 0;
  for (const r of reihen) {
    k += r.kostenCent ?? 0; u += r.umsatzCent ?? 0;
    kosten.push(k); umsatz.push(u);
  }
  return { kosten, umsatz };
}

/** Vorlesetext und Legende der Firmen als Einheiten (ohne Namen). */
export function firmenEinheiten(aktiv: number, gesamt: number): { label: string; legende: { an: string; aus: string } } {
  return { label: W.einheiten(aktiv, gesamt), legende: { an: W.legendeAn(aktiv), aus: W.legendeAus(gesamt - aktiv) } };
}

/** Zeile je Firma auf „Nutzung je Firma“: Unterzeile, Plätze und (mit Euro-Recht) die Kostendeckung. */
export function firmaZeile(f: FirmaStand, ziel: number | null, betrag: Betrag): { unterzeile: string; plaetze: { prozent: number; text: string };
  geld: { prozent: number; text: string; art: 'gut' | 'knapp' } | null } {
  const { belegt, gesamt } = f.plaetze;
  const plaetze = gesamt === null ? { prozent: 0, text: W.plaetzeOhne(zahl(belegt)) }
    : { prozent: gesamt > 0 ? Math.round(belegt * 100 / gesamt) : 0, text: W.plaetze(zahl(belegt), zahl(gesamt)) };
  let geld: { prozent: number; text: string; art: 'gut' | 'knapp' } | null = null;
  if (f.kostenCent !== undefined && f.umsatzCent !== undefined) {
    const k = f.kostenCent, u = f.umsatzCent, d = k > 0 ? Math.round(u * 100 / k) : (u > 0 ? 100 : 0);
    geld = { prozent: Math.min(100, d), text: W.geldZeile(betrag(u), betrag(k), k > 0 ? d : null), art: d >= (ziel ?? 100) ? 'gut' : 'knapp' };
  }
  return { unterzeile: W.firmaUnter(f.tarifName, zahl(f.recherchen), trendText(f.trend)), plaetze, geld };
}

/** Der eine nächste Schritt je Firma (Reihenfolge wie seiten-c.js:209-217); die Umsatzregeln nur mit Euro-Recht. */
export function firmaSchritt(f: FirmaStand, zahlung: { ohneVertrag?: boolean } | undefined, produkt: string): { text: string; link?: SchrittLink } {
  const S = W.schritt, L = W.link, firma = firmaZiel(f.id);
  if (zahlung) return { text: zahlung.ohneVertrag ? S.zahlungOhneVertrag : S.zahlung, link: { text: L.abrechnung, ziel: ABRECHNUNG } };
  if (f.recherchen + f.nachfragen === 0) return { text: S.nichts, link: { text: L.kontakt, ziel: firma } };
  if (f.kostenCent !== undefined && f.umsatzCent !== undefined) {
    if (f.umsatzCent === 0) return { text: S.ohneUmsatz(produkt), link: { text: L.abrechnungPruefen, ziel: ABRECHNUNG } };
    if (f.umsatzCent < f.kostenCent) return { text: S.kostenUeber, link: { text: L.tarif, ziel: firma } };
  }
  if (f.trend.art === 'PROZENT' && f.trend.prozent <= -15) return { text: S.sinkt(trendText(f.trend)), link: { text: L.kontakt, ziel: firma } };
  const { belegt, gesamt } = f.plaetze;
  if (gesamt !== null && gesamt > 0 && belegt / gesamt >= 0.9) return { text: S.plaetzeVoll, link: { text: L.plaetze, ziel: firma } };
  return { text: S.imPlan };
}

/** „Was als Nächstes hilft“ der Übersicht: Umsatz prüfen, überfällige Zahlung, stille Firmen; höchstens 3. */
export function schritteUebersicht(l: { firmen: FirmaStand[] | null; zahlung: PlattformAufmerksamkeit['zahlung']; aktiv: number; mitZugang: number },
  produkt: string): PlattformSchritt[] {
  const liste: PlattformSchritt[] = [], abrechnung: SchrittLink = { text: W.link.abrechnung, ziel: ABRECHNUNG };
  if (l.firmen !== null) {
    const ohne = l.firmen.filter(f => f.recherchen + f.nachfragen > 0 && f.umsatzCent === 0);
    if (ohne.length === 1) {
      const { name, plaetze } = ohne[0];
      liste.push({ key: 'umsatz', symbol: 'geld', stark: W.umsatzPruefen,
        text: W.ohneUmsatzEine(name, plaetze.gesamt === null ? null : `${zahl(plaetze.belegt)} von ${zahl(plaetze.gesamt)}`, produkt), link: abrechnung });
    } else if (ohne.length > 1) {
      liste.push({ key: 'umsatz', symbol: 'geld', stark: W.umsatzPruefen, text: W.ohneUmsatzMehrere(ohne.length, produkt), link: abrechnung });
    }
  }
  if (l.zahlung.length === 1) {
    const eintrag = l.zahlung[0], ohneVertrag = eintrag.ohneVertrag === true;
    liste.push({ key: 'zahlung', symbol: 'achtung', stark: W.zahlungEine(eintrag.name, ohneVertrag), ...(ohneVertrag ? { text: W.ohneVertrag } : {}),
      link: { text: W.link.firma, ziel: firmaZiel(eintrag.id) } });
  } else if (l.zahlung.length > 1) {
    liste.push({ key: 'zahlung', symbol: 'achtung', stark: W.zahlungMehrere(l.zahlung.length), link: abrechnung });
  }
  const still = l.mitZugang - l.aktiv;
  if (still > 0) {
    liste.push({ key: 'still', symbol: 'firma', stark: W.still(still), text: W.stillText, link: { text: W.link.firmen, ziel: { art: 'teil', teil: 'firmen' } } });
  }
  return liste.slice(0, 3);
}

/** Je Tarif als Zielblock: wie viel der KI-Kosten deckt der Umsatz (nur mit Euro-Recht). */
export function tarifZiel(t: TarifStand, ziel: number | null, betrag: Betrag): ZielAngaben {
  const d = t.kostenCent > 0 ? Math.round(t.umsatzCent * 100 / t.kostenCent) : null;
  const kopf = { titel: W.tarifTitel(t.name), gross: d === null ? '–' : prozent(d), von: d === null ? W.tarifOhneKosten : W.tarifDeckt,
    satz: t.recherchenJePlatz === null ? W.keinPlatz : W.jePlatz(zahlEineStelle(t.recherchenJePlatz), t.plaetzeBelegt) };
  if (ziel !== null && d !== null) {
    const p = fortschritt(d, ziel);
    const mitte = d >= ziel ? W.marge(betrag(t.umsatzCent - t.kostenCent)) : W.fehlen(betrag(Math.round(t.kostenCent * ziel / 100) - t.umsatzCent));
    return { ...kopf, prozent: p, skala: { links: '0', mitte, rechts: zielProzent(ziel) }, art: d >= ziel ? 'gut' : 'knapp' };
  }
  return ziel === null ? { ...kopf, prozent: null, art: 'gut', fuss: W.tarifOhneZiel } : { ...kopf, prozent: null, art: 'gut' };
}

/** Die vier Prüfungen (Zahlung, Kontingent, inaktiv, Testphase) als Zielbild „alles in Ordnung“, Zeilen und Schritte. */
export function pruefungenPlattform(a: Pick<PlattformAufmerksamkeit, 'zahlung' | 'kontingent' | 'inaktiv' | 'testphase'>, heute: string):
  { inOrdnung: number; offen: number; ziel: ZielAngaben; zeilen: PruefZeile[]; schritte: PlattformSchritt[] } {
  const P = BERICHT_TEILE.plattform;
  const liste = (namen: string[]) => W.namen(namen.slice(0, 5), namen.length > 5 ? namen.length - 5 : 0);
  const zeile = (key: PruefZeile['key'], titel: string, namen: string[]): PruefZeile =>
    ({ key, art: namen.length > 0 ? 'hinweis' : 'ok', titel: W.pruefung(titel, namen.length), text: namen.length === 0 ? W.keine[key] : liste(namen) });
  const zeilen = [
    zeile('zahlung', AUFMERKSAMKEIT.zahlung, a.zahlung.map(z => z.ohneVertrag ? W.ohneVertragName(z.name) : z.name)),
    zeile('kontingent', AUFMERKSAMKEIT.kontingent, a.kontingent.map(k => W.kontingentName(k.name, prozent(k), k.inRecherchen))),
    zeile('inaktiv', AUFMERKSAMKEIT.inaktiv, a.inaktiv.map(f => f.name)),
    zeile('testphase', AUFMERKSAMKEIT.testphase, a.testphase.map(t => W.testphaseName(t.name, datumWien(t.endet)))),
  ];
  const inOrdnung = zeilen.filter(z => z.art === 'ok').length, offen = zeilen.length - inOrdnung;
  const anzahl = a.zahlung.length + a.kontingent.length + a.inaktiv.length + a.testphase.length;

  // Die eine Firma mit ihrem Grund, wenn es in allen vier Listen zusammen genau einen Eintrag gibt.
  const einzige = (): { name: string; grund: string } => {
    if (a.zahlung.length > 0) return { name: a.zahlung[0].name, grund: a.zahlung[0].ohneVertrag ? W.grundZahlungOhneVertrag : P.grundZahlung };
    if (a.kontingent.length > 0) return { name: a.kontingent[0].name, grund: P.grundKontingent(prozent(a.kontingent[0]), a.kontingent[0].inRecherchen) };
    if (a.inaktiv.length > 0) return { name: a.inaktiv[0].name, grund: P.grundInaktiv };
    return { name: a.testphase[0].name, grund: P.grundTestphase(datumWien(a.testphase[0].endet)) };
  };
  let satz: string;
  if (anzahl === 0) satz = P.keine;
  else if (anzahl === 1) { const { name, grund } = einzige(); satz = W.offenEine(name) + grund; }
  else satz = W.offenMehrere(zeilen.filter(z => z.art === 'hinweis').map(z => z.titel));

  const schritte: PlattformSchritt[] = [];
  if (a.zahlung.length > 0) {
    let text: string = W.zahlungMehrere(a.zahlung.length);
    if (a.zahlung.length === 1) {
      const eintrag = a.zahlung[0];
      const seit = eintrag.seit === null ? null : datumWien(eintrag.seit) === datum(heute) ? W.seitHeute : W.seitTag(datumWien(eintrag.seit));
      text = W.zahlungSchritt(eintrag.name, seit, eintrag.ohneVertrag === true);
    }
    schritte.push({ key: 'zahlung', symbol: 'geld', stark: W.zahlungKlaeren, text, link: { text: W.link.abrechnung, ziel: ABRECHNUNG } });
  }
  if (a.kontingent.length === 1) {
    const k = a.kontingent[0];
    schritte.push({ key: 'kontingent', symbol: 'achtung', stark: W.kontingentAnsprechen, text: `„${k.name}“` + P.grundKontingent(prozent(k), k.inRecherchen),
      link: { text: W.link.firma, ziel: firmaZiel(k.id) } });
  } else if (a.kontingent.length > 1) {
    schritte.push({ key: 'kontingent', symbol: 'achtung', stark: W.kontingentAnsprechen, text: W.kontingentMehrere(a.kontingent.length) });
  }
  if (a.inaktiv.length === 1) {
    const f = a.inaktiv[0];
    schritte.push({ key: 'inaktiv', symbol: 'firma', stark: W.stilleAnsprechen, text: `„${f.name}“` + P.grundInaktiv, link: { text: W.link.firma, ziel: firmaZiel(f.id) } });
  } else if (a.inaktiv.length > 1) {
    schritte.push({ key: 'inaktiv', symbol: 'firma', stark: W.stilleAnsprechen, text: W.inaktivMehrere(a.inaktiv.length) });
  }
  if (a.testphase.length === 1) {
    const t = a.testphase[0];
    schritte.push({ key: 'testphase', symbol: 'kalender', stark: W.testphaseBegleiten, text: `„${t.name}“` + P.grundTestphase(datumWien(t.endet)),
      link: { text: W.link.firma, ziel: firmaZiel(t.id) } });
  } else if (a.testphase.length > 1) {
    schritte.push({ key: 'testphase', symbol: 'kalender', stark: W.testphaseBegleiten, text: W.testphaseMehrere(a.testphase.length) });
  }

  const ziel: ZielAngaben = { titel: W.pruefTitel, gross: zahl(inOrdnung), von: W.pruefVon, prozent: inOrdnung * 25,
    skala: { links: '0', mitte: offen === 0 ? W.allesInOrdnung : W.offen(offen), rechts: ZIEL.zielWert(zahl(zeilen.length)) }, satz, art: 'gut' };
  return { inOrdnung, offen, ziel, zeilen, schritte };
}
